import { Fragment } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useClientFormMeta } from './clientFormMeta';
import { useAddOns } from '../../../../hooks/useAddOns';
import { FIELD_LABELS, STEP_HEADINGS, STORAGE_LENGTH_COPY } from './constants';
import { addOnLengthLabel, termLabel } from './subscriptionTerms';
import { subscriptionLabelFrom } from '../../constants';
import { useSubscriptionTypes } from '../../../../hooks/useSubscriptionTypes';
import type { ClientFormValues } from '../../../../types/ClientManagementTypes';
import styles from './ClientFormModal.module.scss';

const EMPTY_VALUE = '—';

interface SummaryRow {
  /** Distinct from label: an add-on list repeats an empty label across several rows. */
  id: string;
  label: string;
  value: string;
}

const summaryRow = (id: string, label: string, value: string): SummaryRow => ({ id, label, value });

interface SummaryColumnProps {
  rows: SummaryRow[];
}

const SummaryColumn = ({ rows }: SummaryColumnProps) => (
  <div className={styles.summaryColumn}>
    {rows.map((row) => (
      <div key={row.id} className={styles.summaryField}>
        <span className={styles.summaryLabel}>{row.label}</span>
        <span className={styles.summaryValue}>{row.value}</span>
      </div>
    ))}
  </div>
);

interface SummarySectionProps {
  title: string;
  left: SummaryRow[];
  right: SummaryRow[];
  /** Rendered below the fixed rows, inside its own scroll area. */
  addOns?: { names: string[]; lengths: string[] };
}

// A three-column row: the section name sits beside its details rather than above them.
const SummarySection = ({ title, left, right, addOns }: SummarySectionProps) => (
  <div className={styles.summarySection}>
    <h4 className={styles.summarySectionTitle}>{title}</h4>
    <div className={styles.summaryColumns}>
      <SummaryColumn rows={left} />
      <SummaryColumn rows={right} />
    </div>
    {addOns && addOns.names.length > 0 ? (
      <div className={styles.addOnsSection}>
        <span className={styles.summaryLabel}>{FIELD_LABELS.ADD_ONS}</span>
        <span className={styles.summaryLabel}>{FIELD_LABELS.ADD_ON_LENGTH}</span>
        <div className={styles.addOnsScroll}>
          {addOns.names.map((name, index) => (
            <Fragment key={`addOnRow-${index}`}>
              <span className={styles.summaryValue}>{name}</span>
              <span className={styles.summaryValue}>{addOns.lengths[index]}</span>
            </Fragment>
          ))}
        </div>
      </div>
    ) : null}
  </div>
);

const SummaryStep = () => {
  const { currentExpiry } = useClientFormMeta();
  const { control } = useFormContext<ClientFormValues>();
  const { addOnsById } = useAddOns();
  const { subscriptionTypesById } = useSubscriptionTypes();
  const values = useWatch({ control });

  const addOns = values.addOns ?? [];

  // One row per add-on so the two columns stay aligned index for index.
  const addOnNames = addOns.map((row) => (row.addonId !== undefined ? (addOnsById.get(row.addonId)?.name ?? EMPTY_VALUE) : EMPTY_VALUE));
  const addOnLengths = addOns.map((row) => {
    if (row.addonId !== undefined && addOnsById.get(row.addonId)?.hasExpiry === false) return STORAGE_LENGTH_COPY;
    return addOnLengthLabel(row, values.subscriptionTerm ?? '', currentExpiry) || EMPTY_VALUE;
  });

  const paymentStatus = values.paymentMethod ? `${values.paymentMethod} - ${values.markAsPaid ? 'Paid' : 'Pending'}` : EMPTY_VALUE;

  return (
    <section className={styles.step}>
      <h3 className={styles.stepHeading}>{STEP_HEADINGS.SUBSCRIPTION_SUMMARY}</h3>
      <div className={styles.summaryBody}>
        <SummarySection
          title={STEP_HEADINGS.CLIENT_INFORMATION_SHORT}
          left={[
            summaryRow('name', FIELD_LABELS.CLIENT_NAME, values.name || EMPTY_VALUE),
            summaryRow('adminName', FIELD_LABELS.ADMIN_NAME, values.adminName || EMPTY_VALUE),
          ]}
          right={[
            summaryRow('adminEmail', FIELD_LABELS.ADMIN_EMAIL, values.adminEmail || EMPTY_VALUE),
            summaryRow('adminPhone', FIELD_LABELS.ADMIN_PHONE, values.adminPhone || EMPTY_VALUE),
          ]}
        />
        <SummarySection
          title={STEP_HEADINGS.SUBSCRIPTION_INFORMATION_SHORT}
          left={[
            summaryRow(
              'subscriptionType',
              FIELD_LABELS.SUBSCRIPTION_TYPE,
              values.subscriptionType ? subscriptionLabelFrom(subscriptionTypesById, values.subscriptionType) : EMPTY_VALUE,
            ),
          ]}
          right={[
            summaryRow(
              'subscriptionTerm',
              FIELD_LABELS.SUBSCRIPTION_LENGTH,
              values.subscriptionTerm ? termLabel(values.subscriptionTerm, currentExpiry) : EMPTY_VALUE,
            ),
          ]}
          addOns={{ names: addOnNames, lengths: addOnLengths }}
        />
        <SummarySection
          title={STEP_HEADINGS.PAYMENT_INFORMATION_SHORT}
          left={[
            summaryRow('paymentGroupName', FIELD_LABELS.PAYMENT_GROUP, values.paymentGroupName || EMPTY_VALUE),
            summaryRow('contactName', FIELD_LABELS.CONTACT_NAME, values.contactName || EMPTY_VALUE),
            summaryRow('paymentStatus', FIELD_LABELS.PAYMENT_STATUS, paymentStatus),
          ]}
          right={[
            summaryRow('contactEmail', FIELD_LABELS.CONTACT_EMAIL, values.contactEmail || EMPTY_VALUE),
            summaryRow('contactPhone', FIELD_LABELS.CONTACT_PHONE, values.contactPhone || EMPTY_VALUE),
          ]}
        />
      </div>
    </section>
  );
};

export default SummaryStep;
